import { Camera as BABYLON_Camera } from '@inter-ikea-betrakta-babylonjs/core/Cameras/camera.js';
import { AOQualityPreset, ExposurePreset } from '../api.js';
import { AccumulationBuffer } from './Accumulator.js';
import * as Utils from './utility/utils.js';
import { BloomEffect } from './Bloom.js';
import { EdgeSketchEffect } from './utility/EdgeSketchEffect.js';
import { AmbientOcclusion } from './AmbientOcclusion.js';
import { Vector3 as BABYLON_Vector3 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
const CD_PER_RGB = 1;
const AO_WARMUP_FRAME_COUNT = 16;
/** Calculates an exposure value (EV) from camera settings.
 *
 * Parameters:
 * - aperture: AKA f-stop. Dimensionless number describing the size of the aperture. Half the value means four times brighter.
 * - shutter_speed: measured in s^-1, typically 50-500. Half the value means twice as bright.
 * - film_speed: AKA ISO; dimensionless number, typically 100-1600. Twice as high means twice as bright.
 */
export function exposure_value({ aperture, shutter_speed, sensitivity }) {
    return Math.log2(aperture * aperture * shutter_speed / sensitivity * 100);
}
/** Converts a log2 exposure value into a linear scale multiplier, normalized around
 * ev 0 = 1.
 */
export function ev_to_intensity(ev) {
    return CD_PER_RGB / Math.pow(2, ev);
}
export const EXPOSURE_PRESETS = {
    // EV: 6
    [ExposurePreset.HOME_INTERIOR]: {
        aperture: 1.4,
        shutter_speed: 125,
        sensitivity: 400
    },
    // EV: 8
    [ExposurePreset.BRIGHTLY_LIT_OFFICE]: {
        aperture: 2.0,
        shutter_speed: 125,
        sensitivity: 200
    },
    // EV: 12
    [ExposurePreset.OUTDOOR_HEAVY_OVERCAST]: {
        aperture: 2.0,
        shutter_speed: 2000,
        sensitivity: 200
    },
    // EV: 15
    [ExposurePreset.OUTDOOR_CLEAR_DAY]: {
        aperture: 2.8,
        shutter_speed: 4000,
        sensitivity: 100
    }
};
export class PostProcessManager {
    dispose() {
        var _a, _b;
        this._accumulation_buffer.dispose();
        (_a = this._bloom) === null || _a === void 0 ? void 0 : _a.dispose();
        (_b = this._ambient_occlusion) === null || _b === void 0 ? void 0 : _b.dispose();
    }
    constructor(_viewer, framebuffer, ao_supported, scene, engine_camera, mark_view_dirty) {
        this._viewer = _viewer;
        this.framebuffer = framebuffer;
        this.ao_supported = ao_supported;
        this.scene = scene;
        this.engine_camera = engine_camera;
        this.mark_view_dirty = mark_view_dirty;
        this._bloom = null;
        this._ambient_occlusion = null;
        this._gpu_profiling_section_tonemapping = null;
        this._gpu_profiling_section_acummulation_buffer = null;
        /**
         * This is an experimental feature only meant to be used by screenshots.
         * However this was left in here so that you can debug/develop the feature in realtime without using screenhots.
         */
        this.edge_sketch_effect = null;
        // We default to desktop here regardless of what device we're running on.
        // We leave it to the planners to change settings depending on device.
        const default_quality_preset = AOQualityPreset.DESKTOP_DEFAULT;
        const gpu_profiler = this._viewer.get_gpu_profiler();
        if (gpu_profiler !== null) {
            this._gpu_profiling_section_tonemapping = gpu_profiler.create_section('Tonemapping');
            this._gpu_profiling_section_acummulation_buffer = gpu_profiler.create_section('Accumulation');
        }
        this._post_process_camera = new BABYLON_Camera('full_screen_cam', new BABYLON_Vector3(0, 0, -1), scene);
        this.accumulation_frame_count = 16;
        this._accumulation_buffer = new AccumulationBuffer(this.scene, this.accumulation_frame_count, this.framebuffer);
        // Enable AO by default if supported
        this._ao_settings = {
            strength: 1.0,
            radius: 1.0,
            enabled: ao_supported,
            quality: default_quality_preset
        };
        // Enable Bloom by default
        this._bloom_settings = {
            enabled: this.framebuffer.is_float_render_target(), // Enable true by default where supported
            intensity: 1.0
        };
        // Enable bloom by default
        if (this._bloom_settings.enabled) {
            this._bloom = new BloomEffect(scene, this.framebuffer, this._viewer.get_gpu_profiler());
        }
        // Create the AO here instead of using our regular endpoint, as the regular endpoint for creating the AO breaks when called from this constructor.
        if (this._ao_settings.enabled) {
            this._ambient_occlusion = new AmbientOcclusion(scene, this.framebuffer.get_width(), this.framebuffer.get_height(), this._post_process_camera, engine_camera.camera, this.framebuffer.get_depth_texture(), PostProcessManager.create_accumulator_ao_settings(this._ao_settings).samples_per_frame, PostProcessManager.create_accumulator_ao_settings(this._ao_settings).improved_normals, this._viewer.get_gpu_profiler());
            this._ambient_occlusion.ao_radius = this._ao_settings.radius;
            this._accumulation_buffer.queue_warmup_frame_count(AO_WARMUP_FRAME_COUNT);
        }
        else {
            this._ambient_occlusion = null;
        }
    }
    render_ambient_occlusion() {
        if (this._ambient_occlusion) {
            this._ambient_occlusion.ao_radius = this._ao_settings.radius;
            this._ambient_occlusion.ao_strength = this._ao_settings.strength;
            this._ambient_occlusion.render(this.scene);
            this._ambient_occlusion.apply(this.framebuffer);
        }
    }
    render(render_target, depth_texture) {
        var _a, _b, _c;
        if (this._bloom) {
            this._bloom.render(this.scene);
        }
        if (this.edge_sketch_effect) {
            if (depth_texture === null) {
                console.error('No depth texture given for edge rendering - is it not supported?');
            }
            else {
                this.edge_sketch_effect.render(this.engine_camera.camera.getProjectionMatrix(), depth_texture, render_target);
            }
        }
        (_a = this._gpu_profiling_section_acummulation_buffer) === null || _a === void 0 ? void 0 : _a.start();
        // Enable tonemapping if our source data (this.framebuffer) is a float render target
        const enable_tonemapping = this.framebuffer.is_float_render_target();
        this._accumulation_buffer.render(this._bloom, this._viewer.lut_effect, enable_tonemapping);
        (_b = this._gpu_profiling_section_acummulation_buffer) === null || _b === void 0 ? void 0 : _b.stop();
        (_c = this._ambient_occlusion) === null || _c === void 0 ? void 0 : _c.flip_flop();
    }
    /**
     * @returns The texture where the final image was produced
     */
    get_destination_texture() {
        return this._accumulation_buffer.accumulation_buffer2;
    }
    blit_to_framebuffer(target) {
        var _a, _b;
        (_a = this._gpu_profiling_section_tonemapping) === null || _a === void 0 ? void 0 : _a.start();
        this.framebuffer.blit(target);
        (_b = this._gpu_profiling_section_tonemapping) === null || _b === void 0 ? void 0 : _b.stop();
    }
    get ao_settings() {
        return this._ao_settings;
    }
    get bloom_settings() {
        return this._bloom_settings;
    }
    get_active_accumulation_buffer() {
        return this._accumulation_buffer;
    }
    static get_accumulator_viewport_info(canvas_width, canvas_height, viewport_size, viewport_offset) {
        const normalized_pixel_width = viewport_size.x / canvas_width;
        const normalized_pixel_height = viewport_size.y / canvas_height;
        const normalized_pixel_x_offset = viewport_offset.x / canvas_width;
        let normalized_pixel_y_offset = -viewport_offset.y / canvas_height;
        normalized_pixel_y_offset += (canvas_height - viewport_size.y) / canvas_height;
        return {
            width: canvas_width,
            height: canvas_height,
            offset: { x: normalized_pixel_x_offset, y: normalized_pixel_y_offset },
            scale: { x: normalized_pixel_width, y: normalized_pixel_height }
        };
    }
    static create_accumulator_ao_settings(ao_settings) {
        const quality = PostProcessManager.get_ao_quality_preset_settings(ao_settings.quality);
        return {
            radius: ao_settings.radius,
            enabled: ao_settings.enabled,
            strength: ao_settings.strength,
            samples_per_frame: quality.samples_per_frame,
            improved_normals: quality.improved_normals
        };
    }
    // Updates all effects sizes based on the framebuffer.
    resize() {
        var _a;
        this._accumulation_buffer.resize(this.framebuffer.get_width(), this.framebuffer.get_height());
        (_a = this._bloom) === null || _a === void 0 ? void 0 : _a.resize(this.framebuffer.get_width(), this.framebuffer.get_height());
        if (this._ambient_occlusion !== null) {
            this._ambient_occlusion.resize(this.framebuffer.get_width(), this.framebuffer.get_height());
            this._accumulation_buffer.queue_warmup_frame_count(AO_WARMUP_FRAME_COUNT);
        }
    }
    reset_stable_frames() {
        this._accumulation_buffer.reset_stable_frames();
    }
    create_accumulation_buffer() {
        this._accumulation_buffer.set_accumulation_frame_count(this.accumulation_frame_count);
        this.mark_view_dirty();
        this.reset_stable_frames();
    }
    remove_accumulation_buffer() {
        this._accumulation_buffer.set_accumulation_frame_count(0);
        this.mark_view_dirty();
    }
    enable_temporal_effects(use_ambient_occlusion) {
        if (use_ambient_occlusion && !this.ao_supported) {
            use_ambient_occlusion = false;
        }
        this._ao_settings.enabled = use_ambient_occlusion;
        this.create_accumulation_buffer();
        this.set_temporal_ao_parameters(this._ao_settings);
    }
    disable_temporal_effects() {
        this.remove_accumulation_buffer();
    }
    /**
     * Enabled the experimental edge sketch effect
     */
    enable_edge_sketch_effect() {
        if (this.edge_sketch_effect !== null)
            return;
        this.edge_sketch_effect = new EdgeSketchEffect(this.scene);
        this.mark_view_dirty();
    }
    /**
     * Disables the experimental edge sketch effect
     */
    disable_edge_sketch_effect() {
        if (this.edge_sketch_effect === null)
            return;
        this.edge_sketch_effect.dispose();
        this.edge_sketch_effect = null;
        this.mark_view_dirty();
    }
    keep_rendering() {
        return this._accumulation_buffer.keep_rendering();
    }
    set_temporal_ao_parameters(ao_settings) {
        this._ao_settings = Utils.update_from_partial(this._ao_settings, ao_settings);
        if (ao_settings.enabled && !this.ao_supported) {
            console.warn('Ambient Occlusion is not supported on this device');
            return;
        }
        if (this._ao_settings.enabled && this._ambient_occlusion === null) {
            this._ambient_occlusion = new AmbientOcclusion(this.engine_camera.camera._scene, this.framebuffer.get_width(), this.framebuffer.get_height(), this._post_process_camera, this.engine_camera.camera, this.framebuffer.get_depth_texture(), PostProcessManager.create_accumulator_ao_settings(this._ao_settings).samples_per_frame, PostProcessManager.create_accumulator_ao_settings(this._ao_settings).improved_normals, this._viewer.get_gpu_profiler());
            this._accumulation_buffer.queue_warmup_frame_count(AO_WARMUP_FRAME_COUNT);
        }
        else if (!this._ao_settings.enabled && this._ambient_occlusion !== null) {
            this._ambient_occlusion.dispose();
            this._ambient_occlusion = null;
        }
        if (this._ambient_occlusion) {
            this._ambient_occlusion.enable_normal_filtering = PostProcessManager.create_accumulator_ao_settings(this._ao_settings).improved_normals;
            this._ambient_occlusion.set_ao_sample_count(PostProcessManager.create_accumulator_ao_settings(this._ao_settings).samples_per_frame);
            this._ambient_occlusion.ao_radius = this._ao_settings.radius;
            this._ambient_occlusion.ao_strength = this._ao_settings.strength;
        }
        this.mark_view_dirty();
    }
    on_scene_dirty() {
        if (this._ambient_occlusion !== null) {
            this._accumulation_buffer.queue_warmup_frame_count(AO_WARMUP_FRAME_COUNT);
        }
    }
    set_temporal_accumulation_frame_count(frame_count) {
        this.accumulation_frame_count = Math.max(1, frame_count);
        this._accumulation_buffer.set_accumulation_frame_count(this.accumulation_frame_count);
        this.mark_view_dirty();
    }
    jitter(viewport_width, viewport_height) {
        this._accumulation_buffer.jitter(viewport_width, viewport_height, this.engine_camera);
    }
    is_taa_enabled() {
        return this._accumulation_buffer.get_accumulation_frame_count() > 1;
    }
    increase_stable_frames() {
        this._accumulation_buffer.increase_stable_frames();
    }
    set_bloom(enable, intensity) {
        if (!enable && this._bloom === null)
            return false;
        if (enable && this._bloom !== null && (intensity === undefined || intensity === this._bloom.intensity))
            return false;
        this._bloom_settings.enabled = enable;
        if (intensity !== undefined) {
            this._bloom_settings.intensity = intensity;
        }
        if (!enable && this._bloom !== null) {
            this._bloom.dispose();
            this._bloom = null;
        }
        else if (enable && this._bloom === null) {
            if (!this.framebuffer.is_float_render_target()) {
                // Bloom isn't supported
                console.warn('Bloom required a float render target! It might not be supported on this device.');
                return false;
            }
            this._bloom = new BloomEffect(this.scene, this.framebuffer, this._viewer.get_gpu_profiler());
            if (intensity !== undefined) {
                this._bloom.intensity = intensity;
            }
        }
        else if (enable && this._bloom !== null) {
            // For some reason typescript doesn't understand that intensity is guaranteed to be defined.
            this._bloom.intensity = intensity;
        }
        this.mark_view_dirty();
        return true;
    }
    static get_ao_quality_preset_settings(preset) {
        // For some reason a map didn't work with the enum when declared and initialized outside of the class. That's why this is a function.
        switch (preset) {
            case AOQualityPreset.DESKTOP_LOW:
                return {
                    samples_per_frame: 8,
                    improved_normals: true
                };
            case AOQualityPreset.DESKTOP_DEFAULT:
            case AOQualityPreset.DESKTOP_MEDIUM:
                return {
                    samples_per_frame: 12,
                    improved_normals: true
                };
            case AOQualityPreset.DESKTOP_HIGH:
                return {
                    samples_per_frame: 16,
                    improved_normals: true
                };
            case AOQualityPreset.MOBILE_LOW:
                return {
                    samples_per_frame: 6,
                    improved_normals: false
                };
            case AOQualityPreset.MOBILE_DEFAULT: // Fallthrough
            case AOQualityPreset.MOBILE_MEDIUM:
                return {
                    samples_per_frame: 8,
                    improved_normals: false
                };
            case AOQualityPreset.MOBILE_HIGH:
                return {
                    samples_per_frame: 12,
                    improved_normals: true
                };
            case AOQualityPreset.SCREENSHOT:
                return {
                    samples_per_frame: 32,
                    improved_normals: true
                };
            default:
                console.warn('Failed to recognize AO quality preset: ' + preset + '. Returning medium desktop settings.');
                return {
                    samples_per_frame: 12,
                    improved_normals: true
                };
        }
    }
}
//# sourceMappingURL=PostProcessManager.js.map